import { useEffect, useState, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Dropdown,
  Option,
  Spinner,
  MessageBar,
  MessageBarBody,
  Tag,
  Field,
} from '@fluentui/react-components';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { useAuthStore } from '../stores/authStore.js';
import {
  listStaffTenantsUsage,
  type TenantUsageSummary,
} from '../api/staffUsage.js';
import { ApiClientError } from '../api/client.js';
import { formatUsd, fmtDate, monthRangeFromInput, todayIsoEndOfDay, monthsAgoIso } from '../lib/staffUsageFormat.js';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  content: { flex: 1, padding: '24px 32px', maxWidth: '1280px', width: '100%', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px', flexWrap: 'wrap' },
  title: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold },
  filters: { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' },
  filterField: { minWidth: '160px' },
  tableWrap: { border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, overflow: 'hidden', background: tokens.colorNeutralBackground1 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 14px', background: tokens.colorNeutralBackground2, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' },
  thRight: { textAlign: 'right' },
  td: { padding: '10px 14px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, fontSize: tokens.fontSizeBase300 },
  tdRight: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  link: { color: tokens.colorBrandForegroundLink, textDecoration: 'none', fontWeight: tokens.fontWeightSemibold, ':hover': { textDecoration: 'underline' } },
  emptyRow: { padding: '24px 14px', textAlign: 'center', color: tokens.colorNeutralForeground3 },
  totalsBar: { display: 'flex', gap: '24px', padding: '12px 14px', background: tokens.colorNeutralBackground2, borderTop: `1px solid ${tokens.colorNeutralStroke2}`, fontVariantNumeric: 'tabular-nums' },
});

const PLAN_LABEL: Record<string, string> = {
  start: 'Start',
  pro: 'Pro',
  teams: 'Teams',
  vitruvio: 'Vitruvio',
};

export function StaffUsagePage() {
  const styles = useStyles();
  const tenant = useAuthStore((s) => s.tenant);
  const isStaff =
    !!tenant?.isEcosoftOwned && (tenant.role === 'admin' || tenant.role === 'owner');

  const [from, setFrom] = useState<string>(() => monthsAgoIso(11));
  const [to, setTo] = useState<string>(() => todayIsoEndOfDay());
  const [planFilter, setPlanFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [tenants, setTenants] = useState<TenantUsageSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isStaff) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listStaffTenantsUsage({
      from: monthRangeFromInput(from, 'start'),
      to: monthRangeFromInput(to, 'end'),
      plan: planFilter ? (planFilter as 'start' | 'pro' | 'teams' | 'vitruvio') : undefined,
      search: search.trim() || undefined,
    })
      .then((res) => {
        if (!cancelled) setTenants(res.tenants);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiClientError ? e.message : 'Error al cargar tenants');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isStaff, from, to, planFilter, search]);

  const totals = useMemo(() => {
    if (!tenants) return { period: 0, annual: 0, turns: 0 };
    return tenants.reduce(
      (acc, t) => ({
        period: acc.period + t.periodCostUsd,
        annual: acc.annual + t.annualCostUsd,
        turns: acc.turns + t.periodTurns,
      }),
      { period: 0, annual: 0, turns: 0 },
    );
  }, [tenants]);

  if (!isStaff) return <Navigate to="/" replace />;

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs
        items={[
          { label: 'Inicio', to: '/projects' },
          { label: 'Staff' },
          { label: 'Consumo por tenant' },
        ]}
      />
      <main className={styles.content}>
        <div className={styles.header}>
          <Text as="h1" className={styles.title}>Consumo por tenant</Text>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Link to="/staff/usage/runs" className={styles.link}>Costo por corrida (fases) →</Link>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              Vista restringida — Ecosoft staff
            </Text>
          </div>
        </div>

        <div className={styles.filters}>
          <Field label="Desde" className={styles.filterField}>
            <Input
              type="date"
              value={from.slice(0, 10)}
              onChange={(_e, d) => setFrom(d.value)}
            />
          </Field>
          <Field label="Hasta" className={styles.filterField}>
            <Input
              type="date"
              value={to.slice(0, 10)}
              onChange={(_e, d) => setTo(d.value)}
            />
          </Field>
          <Field label="Plan" className={styles.filterField}>
            <Dropdown
              value={planFilter ? PLAN_LABEL[planFilter] : 'Todos'}
              selectedOptions={planFilter ? [planFilter] : []}
              onOptionSelect={(_e, d) => setPlanFilter((d.optionValue as string) ?? '')}
            >
              <Option value="">Todos</Option>
              <Option value="start">Start</Option>
              <Option value="pro">Pro</Option>
              <Option value="teams">Teams</Option>
              <Option value="vitruvio">Vitruvio</Option>
            </Dropdown>
          </Field>
          <Field label="Búsqueda" className={styles.filterField}>
            <Input
              placeholder="slug o nombre…"
              value={search}
              onChange={(_e, d) => setSearch(d.value)}
            />
          </Field>
        </div>

        {error && (
          <MessageBar intent="error" style={{ marginBottom: 16 }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Empresa</th>
                <th className={styles.th}>Plan</th>
                <th className={styles.th}>Suscripción</th>
                <th className={`${styles.th} ${styles.thRight}`}>$ período</th>
                <th className={`${styles.th} ${styles.thRight}`}>$ anual</th>
                <th className={`${styles.th} ${styles.thRight}`}>Turnos</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className={styles.emptyRow} colSpan={6}>
                    <Spinner size="tiny" label="Cargando…" />
                  </td>
                </tr>
              )}
              {!loading && tenants && tenants.length === 0 && (
                <tr>
                  <td className={styles.emptyRow} colSpan={6}>
                    Sin resultados.
                  </td>
                </tr>
              )}
              {!loading && tenants?.map((t) => (
                <tr key={t.tenantId}>
                  <td className={styles.td}>
                    <Link to={`/staff/usage/tenants/${t.tenantId}`} className={styles.link}>
                      {t.slug}
                    </Link>
                    <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
                      {t.name}
                    </div>
                  </td>
                  <td className={styles.td}>
                    <Tag size="small">{t.plan ? PLAN_LABEL[t.plan] ?? t.plan : '—'}</Tag>
                  </td>
                  <td className={styles.td}>
                    {t.subscriptionExpiresAt ? (
                      <span title={`Inicio: ${fmtDate(t.subscriptionStartedAt) ?? '?'}`}>
                        vence {fmtDate(t.subscriptionExpiresAt)}
                      </span>
                    ) : (
                      <span style={{ color: tokens.colorNeutralForeground3 }}>—</span>
                    )}
                  </td>
                  <td className={`${styles.td} ${styles.tdRight}`}>{formatUsd(t.periodCostUsd)}</td>
                  <td className={`${styles.td} ${styles.tdRight}`}>{formatUsd(t.annualCostUsd)}</td>
                  <td className={`${styles.td} ${styles.tdRight}`}>{t.periodTurns.toLocaleString('es-MX')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {tenants && tenants.length > 0 && (
            <div className={styles.totalsBar}>
              <span><strong>Total período:</strong> {formatUsd(totals.period)}</span>
              <span><strong>Total anual:</strong> {formatUsd(totals.annual)}</span>
              <span><strong>Turnos:</strong> {totals.turns.toLocaleString('es-MX')}</span>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
